import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { staggeredBaseQuery } from "./staggeredBaseQuery";
import { ComplianceRosterResponse, ComplianceTraining, ComplianceTrainingFleetRule, ComplianceTrainingRecord, ComplianceTrainingReportRow } from "../../common/ComplianceTraining";

export const complianceTrainingApi = createApi({
    reducerPath: "complianceTrainingApi",
    tagTypes: ["ComplianceTraining", "ComplianceTrainingRules"],
    baseQuery: staggeredBaseQuery(""),
    endpoints: (builder) => ({
        getComplianceRoster: builder.query<ComplianceRosterResponse, string>({
            query: (vesselId) => ({
                method: "GET",
                url: `complianceTrainings/roster?vesselId=${vesselId}`,
            }),
            providesTags: [{ type: "ComplianceTraining", id: "ROSTER" }],
        }),
        uploadComplianceTrainingDocument: builder.mutation<void, FormData>({
            query: (data) => ({
                method: "POST",
                url: "complianceTrainings/records",
                body: data,
            }),
            invalidatesTags: [{ type: "ComplianceTraining", id: "ROSTER" }],
        }),
        getComplianceTrainingReport: builder.query<ComplianceTrainingReportRow[], void>({
            query: () => ({
                method: "GET",
                url: "complianceTrainings/report",
            }),
        }),
        getFleetRules: builder.query<ComplianceTrainingFleetRule[], number>({
            query: (trainingId) => ({
                method: "GET",
                url: `complianceTrainings/${trainingId}/fleetRules`,
            }),
            providesTags: (_result, _err, id) => [{ type: "ComplianceTrainingRules", id }],
        }),
        updateRequiredRatings: builder.mutation<void, { trainingId: number; ratings: string[] }>({
            query: ({ trainingId, ratings }) => ({
                method: "PUT",
                url: `complianceTrainings/${trainingId}/requiredRatings`,
                body: ratings,
            }),
            invalidatesTags: [
                { type: "ComplianceTraining", id: "ROSTER" },
                { type: "ComplianceTraining", id: "LIST" },
            ],
        }),
        updateFleetRules: builder.mutation<void, { trainingId: number; rules: ComplianceTrainingFleetRule[] }>({
            query: ({ trainingId, rules }) => ({
                method: "PUT",
                url: `complianceTrainings/${trainingId}/fleetRules`,
                body: rules,
            }),
            invalidatesTags: (_result, _err, { trainingId }) => [
                { type: "ComplianceTrainingRules", id: trainingId },
                { type: "ComplianceTraining", id: "ROSTER" },
            ],
        }),
        listComplianceTrainings: builder.query<ComplianceTraining[], boolean | void>({
            query: (includeDeleted) => ({
                method: "GET",
                url: `complianceTrainings${includeDeleted ? "?includeDeleted=true" : ""}`,
            }),
            providesTags: [{ type: "ComplianceTraining", id: "LIST" }],
        }),
        createComplianceTraining: builder.mutation<ComplianceTraining, { name: string; validityInMonths: number | null; documentRequired: boolean }>({
            query: (body) => ({
                method: "POST",
                url: "complianceTrainings",
                body,
            }),
            invalidatesTags: [{ type: "ComplianceTraining", id: "LIST" }],
        }),
        updateComplianceTraining: builder.mutation<ComplianceTraining, { id: number; name: string; validityInMonths: number | null; documentRequired: boolean }>({
            query: ({ id, ...body }) => ({
                method: "PUT",
                url: `complianceTrainings/${id}`,
                body,
            }),
            invalidatesTags: [{ type: "ComplianceTraining", id: "LIST" }],
        }),
        deleteComplianceTraining: builder.mutation<void, number>({
            query: (id) => ({
                method: "DELETE",
                url: `complianceTrainings/${id}`,
            }),
            invalidatesTags: [{ type: "ComplianceTraining", id: "LIST" }],
        }),
        restoreComplianceTraining: builder.mutation<void, number>({
            query: (id) => ({
                method: "PATCH",
                url: `complianceTrainings/${id}/restore`,
            }),
            invalidatesTags: [{ type: "ComplianceTraining", id: "LIST" }],
        }),
        getProfileComplianceRecords: builder.query<ComplianceTrainingRecord[], number>({
            query: (profileId) => ({
                method: "GET",
                url: `complianceTrainings/profiles/${profileId}/records`,
            }),
            providesTags: (_result, _err, profileId) => [{ type: "ComplianceTraining", id: `PROFILE_${profileId}` }],
        }),
        updateComplianceTrainingRecord: builder.mutation<ComplianceTrainingRecord, { id: number; complianceTraining: { id: number }; issueDate: string; expirationDate: string }>({
            query: ({ id, ...body }) => ({
                method: "PUT",
                url: `complianceTrainings/records/${id}`,
                body,
            }),
            invalidatesTags: [{ type: "ComplianceTraining" }],
        }),
        deleteComplianceTrainingRecord: builder.mutation<void, number>({
            query: (id) => ({
                method: "DELETE",
                url: `complianceTrainings/records/${id}`,
            }),
            invalidatesTags: [{ type: "ComplianceTraining" }],
        }),
        downloadComplianceTrainingRecord: builder.mutation<Blob, number>({
            query: (id) => ({
                method: "GET",
                url: `complianceTrainings/records/${id}/document`,
                responseHandler: (response) => response.blob(),
            }),
        }),
    }),
});

export const {
    useGetComplianceRosterQuery,
    useUploadComplianceTrainingDocumentMutation,
    useGetComplianceTrainingReportQuery,
    useGetFleetRulesQuery,
    useUpdateRequiredRatingsMutation,
    useUpdateFleetRulesMutation,
    useListComplianceTrainingsQuery,
    useCreateComplianceTrainingMutation,
    useUpdateComplianceTrainingMutation,
    useDeleteComplianceTrainingMutation,
    useRestoreComplianceTrainingMutation,
    useGetProfileComplianceRecordsQuery,
    useUpdateComplianceTrainingRecordMutation,
    useDeleteComplianceTrainingRecordMutation,
    useDownloadComplianceTrainingRecordMutation,
} = complianceTrainingApi;
